// ─────────────────────────────────────────────────────────────────────────────
//  settlement.js  —  Torch App · Pick Settlement Engine
//
//  Points system:
//    • Each pick has a `wager` (points wagered from user balance)
//    • WIN  → payout = wager × decimalOdds  (profit = payout − wager)
//    • LOSS → lose full wager
//    • PUSH → wager returned (no gain/loss)
//    • Balance floor: −200 (can't go below)
//    • Friday payday: +200 pts added weekly
//
//  ⚠️  BALANCE ACCOUNTING — READ CAREFULLY:
//    At pick submission, the wager is deducted from the user's balance.
//    At settlement, we must credit back the FULL PAYOUT (stake + profit) on a win,
//    or JUST the stake on a push. The Firestore `points` field stores only the
//    net profit for UI display, but the balance delta must include the stake return.
//
//    Example: 100pt wager at -110 odds (decimal 1.909)
//      submit   → balance −100  (wager deducted)
//      win      → balance +191  (full payout: stake 100 + profit 91)
//      net      → +91 ✓
//
//    The `points` field (profit) drives the "+91 pts" display in the UI.
//    The balance delta (full payout) is what's actually credited to the user's balance.
// ─────────────────────────────────────────────────────────────────────────────

import {
  getFirestore, doc, getDoc, updateDoc, setDoc,
  collection, addDoc, getDocs, query, orderBy, limit, serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import app from "./firebase";
import { fetchScores } from "./sportsApi";
import { getPlayerStatForSettlement, getGameScoreFromESPN } from "./espnStats";

const db = getFirestore(app);

// ⚠️ toDate -- convert anything-that-might-be-a-timestamp into a Date.
// Picks from Firestore have commenceTime as a Firestore Timestamp (when
// written by a Cloud Function) OR an ISO string (when written by the
// client). `new Date(Timestamp)` returns Invalid Date silently. Use this
// for ALL date arithmetic on pick fields.
//
// This bit us on the v1 cron settlement fix and is bitting again on the
// client where the date-drift guards were no-oping for any pick written
// by a CF (hops, restored-from-report, etc).
function toDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return isNaN(ts) ? null : ts;
  if (typeof ts.toDate === "function") {
    try { const d = ts.toDate(); return isNaN(d) ? null : d; } catch { return null; }
  }
  if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
  if (typeof ts._seconds === "number") return new Date(ts._seconds * 1000);
  if (typeof ts === "number") { const d = new Date(ts); return isNaN(d) ? null : d; }
  if (typeof ts === "string") { const d = new Date(ts); return isNaN(d) ? null : d; }
  return null;
}

export const BALANCE_FLOOR    = 0;
export const WEEKLY_PAYDAY    =  200;
export const STARTING_BALANCE = 1000; // Points seeded to new users on first login
const PAYDAY_DAY = 5; // Friday (0=Sun)

// ─── ODDS HELPERS ─────────────────────────────────────────────────────────────
export function americanToDecimal(american) {
  // FIX: treat null AND 0 as "odds missing" — default to -110 (1.909) not even money (2.0).
  // Picks.js now stores null for missing odds (not 0), but we guard 0 here too for
  // any legacy picks already in Firestore with odds=0.
  if (american == null || american === 0) {
    console.warn('[Settlement] americanToDecimal: odds missing/zero — defaulting to -110');
    return 1.9091; // -110 standard vig
  }
  // F4 PARITY: return the FULL-PRECISION decimal — NO .toFixed() rounding.
  // The server (functions/index.js) computes this un-rounded, and either engine
  // may be the one that settles a given pick (client on load vs the 5-min cron,
  // whichever flips status=pending first). A 4-dp truncation here made the two
  // engines disagree by ±1 pt on "ugly" odds (e.g. -144, -236, -566) — the exact
  // odds real prop/live markets return. It also made the submit-time payout
  // preview (calcPayout below, used by the slip drawer) mismatch the amount the
  // server actually credits. Un-rounded matches the exact rational payout in
  // ~97.6% of the diverging cases; .toFixed(4) matched in ~2.4%. Keep these two
  // lines byte-identical to functions/index.js americanToDecimal().
  if (american > 0) return 1 + american / 100;
  return 1 + 100 / Math.abs(american);
}

export function calcPayout(wager, american) {
  return Math.round(wager * americanToDecimal(american));
}

// netPoints: NET PROFIT for display/Firestore `points` field.
// This is intentionally profit-only — the balance delta is handled separately
// in settleUserPicks via balanceDeltaForResult() to include the stake return.
export function netPoints(status, wager, american) {
  if (!wager || wager <= 0) return 0;
  if (status === 'push') return 0;
  if (status === 'lost') return 0;
  if (status === 'won')  return calcPayout(wager, american) - Math.round(wager);
  return 0;
}

// ─── BALANCE DELTA HELPER ─────────────────────────────────────────────────────
// Returns what is actually ADDED to the user's balance at settlement.
// WIN  → full payout (stake + profit) — stake was deducted at submit time
// PUSH → stake refund (stake was deducted, no profit earned)
// LOSS → 0 (stake already gone, nothing to credit back)
function balanceDeltaForResult(status, wager, american) {
  if (!wager || wager <= 0) return 0;
  if (status === 'won')  return calcPayout(wager, american); // stake + profit
  if (status === 'push') return Math.round(wager);           // stake refund only
  return 0;
}

// ─── ATOMIC SETTLE + CREDIT (fix 1.5, 2026-09-28) ─────────────────────────────
// Before this, each pick's STATUS was written in its own transaction while the
// balance credit for the whole run was applied once at the very end, and the
// league member credit (balance, W/L, streak) later still, by the caller in
// TorchApp. Anything that stopped the run in between -- iOS suspending the
// WebView, a closed tab, a thrown error -- left picks showing won/push with
// the payout never credited, and nothing retried it (the client reconciler was
// removed in Round 8; the server's ledger only covers picks the server settled).
//
// Now ONE transaction per pick re-checks status == "pending", flips it, credits
// the global balance and the league member (balance + W/L + streak), and stamps
// leagueCredited. Either all of that lands or none of it does. Same semantics
// as the server's settleAllPicks credit path (floored balances, wins/streak on
// won, losses + streak reset on lost, pushes move neither) plus the client's
// existing bestStreak bookkeeping. Every write here is already allowed by
// firestore.rules (pick-update whitelist, owner user doc, owner member doc).
//
// Torch/Spot Me picks are left to the server (settleTorchLoanAdmin splits the
// payout between two users); the callers already filter torch, this makes it
// hold for any future caller too.
async function settleAndCredit(userId, pick, pickFields, status, balChg) {
  if (pick.isTorchLoan || pick.isSpotMe || pick.isTorchMirror) return { did: false };
  const pickRef   = doc(db, 'picks', userId, 'userPicks', pick.id);
  const userRef   = doc(db, 'users', userId);
  const memberRef = pick.leagueId ? doc(db, 'leagues', pick.leagueId, 'members', userId) : null;
  let did = false, applied = 0, nextBal = null;
  await runTransaction(db, async (txn) => {
    did = false; applied = 0; nextBal = null; // the SDK may re-run this body
    // ── READS (all before any write) ──
    const pSnap = await txn.get(pickRef);
    if (pSnap.data()?.status !== 'pending') {
      console.warn(`[Settlement] Pick ${pick.id} already settled — skipping`);
      return;
    }
    // PRACTICE-ONLY-GLOBAL (fix 2.6, 2026-09-28): users.balance is the practice
    // balance; only practice (no-league) picks credit it. Mirrors the server.
    const creditsGlobal = balChg !== 0 && !pick.leagueId;
    const uSnap = creditsGlobal ? await txn.get(userRef) : null;
    const mSnap = memberRef ? await txn.get(memberRef) : null;
    // ── WRITES ──
    txn.update(pickRef, { ...pickFields, ...(memberRef ? { leagueCredited: true } : {}) });
    if (creditsGlobal) {
      const cur = uSnap.data()?.balance ?? 0;
      nextBal = Math.max(BALANCE_FLOOR, cur + balChg);
      applied = nextBal - cur;
      txn.set(userRef, { balance: nextBal }, { merge: true });
    }
    if (mSnap && mSnap.exists()) {
      const m = mSnap.data() || {};
      const upd = {};
      if (balChg !== 0) upd.balance = Math.max(0, (m.balance ?? 0) + balChg);
      if (status === 'won') {
        const streak = (m.streak ?? 0) + 1;
        upd.wins = (m.wins ?? 0) + 1;
        upd.streak = streak;
        if (streak > (m.bestStreak ?? 0)) upd.bestStreak = streak;
      } else if (status === 'lost') {
        upd.losses = (m.losses ?? 0) + 1;
        upd.streak = 0;
      }
      if (Object.keys(upd).length > 0) txn.update(memberRef, upd);
    }
    did = true;
  });
  if (did && applied !== 0) {
    // Best-effort audit row, outside the transaction (same as applyBalanceDelta).
    addDoc(collection(db, 'users', userId, 'balanceHistory'), {
      balance: nextBal, delta: applied, label: status === 'push' ? 'push' : 'win', pickId: pick.id, createdAt: new Date(),
    }).catch(() => {});
  }
  return { did, leagueCredited: did && !!memberRef };
}

// ─── SPORT KEY MAP ────────────────────────────────────────────────────────────
// Maps display labels → Odds API sport keys. Used as fallback when pick.sportKey
// is missing (legacy picks). Modern picks store sportKey directly at submission.
const SPORT_TO_KEY = {
  // US Major Leagues
  NBA: 'basketball_nba', WNBA: 'basketball_wnba',
  NFL: 'americanfootball_nfl', MLB: 'baseball_mlb', NHL: 'icehockey_nhl',
  // College
  NCAAB: 'basketball_ncaab', NCAAF: 'americanfootball_ncaaf',
  WNCAAB: 'basketball_wncaab',
  // US Other
  CFL: 'americanfootball_cfl', UFL: 'americanfootball_ufl',
  'NCAA BB': 'baseball_ncaa', PLL: 'lacrosse_pll', 'NCAA LAX': 'lacrosse_ncaa',
  // International Baseball
  MiLB: 'baseball_milb', NPB: 'baseball_npb', KBO: 'baseball_kbo',
  // International Basketball
  Euroleague: 'basketball_euroleague', NBL: 'basketball_nbl',
  // International Hockey
  AHL: 'icehockey_ahl', Liiga: 'icehockey_liiga', Mestis: 'icehockey_mestis',
  SHL: 'icehockey_sweden_hockey_league', HockeyAllsv: 'icehockey_sweden_allsvenskan',
  // Combat
  MMA: 'mma_mixed_martial_arts', Boxing: 'boxing_boxing',
  // Soccer — Top Leagues
  MLS: 'soccer_usa_mls', EPL: 'soccer_epl', UCL: 'soccer_uefa_champs_league',
  'UCL Qual': 'soccer_uefa_champs_league_qualification', UWCL: 'soccer_uefa_champs_league_women',
  UEL: 'soccer_uefa_europa_league', UECL: 'soccer_uefa_europa_conference_league',
  'La Liga': 'soccer_spain_la_liga', 'Serie A': 'soccer_italy_serie_a',
  Bundesliga: 'soccer_germany_bundesliga', 'Ligue 1': 'soccer_france_ligue_one',
  // Soccer — Lower Divisions & Cups
  Championship: 'soccer_efl_champ', 'EFL L1': 'soccer_england_league1',
  'EFL L2': 'soccer_england_league2', 'EFL Cup': 'soccer_england_efl_cup',
  'FA Cup': 'soccer_fa_cup',
  'Bundesliga 2': 'soccer_germany_bundesliga2', '3. Liga': 'soccer_germany_liga3',
  'DFB-Pokal': 'soccer_germany_dfb_pokal', 'Frauen BL': 'soccer_germany_bundesliga_women',
  'La Liga 2': 'soccer_spain_segunda_division', 'Copa del Rey': 'soccer_spain_copa_del_rey',
  'Serie B': 'soccer_italy_serie_b', 'Coppa Italia': 'soccer_italy_coppa_italia',
  'Ligue 2': 'soccer_france_ligue_two', 'Coupe de Fr': 'soccer_france_coupe_de_france',
  // Soccer — Other European
  Eredivisie: 'soccer_netherlands_eredivisie', Primeira: 'soccer_portugal_primeira_liga',
  'Turkey SL': 'soccer_turkey_super_league', Scotland: 'soccer_spl',
  Belgium: 'soccer_belgium_first_div', 'Swiss SL': 'soccer_switzerland_superleague',
  Denmark: 'soccer_denmark_superliga', Sweden: 'soccer_sweden_allsvenskan',
  Superettan: 'soccer_sweden_superettan', Norway: 'soccer_norway_eliteserien',
  Finland: 'soccer_finland_veikkausliiga', Greece: 'soccer_greece_super_league',
  Poland: 'soccer_poland_ekstraklasa', Austria: 'soccer_austria_bundesliga',
  'Russia PL': 'soccer_russia_premier_league', Ireland: 'soccer_league_of_ireland',
  'Saudi PL': 'soccer_saudi_arabia_pro_league',
  // Soccer — Americas
  Brasileirao: 'soccer_brazil_campeonato', 'Brazil B': 'soccer_brazil_serie_b',
  'Liga MX': 'soccer_mexico_ligamx', Argentina: 'soccer_argentina_primera_division',
  Chile: 'soccer_chile_campeonato',
  'Copa Lib': 'soccer_conmebol_copa_libertadores', 'Copa Sud': 'soccer_conmebol_copa_sudamericana',
  'Gold Cup': 'soccer_concacaf_gold_cup', 'Leagues Cup': 'soccer_concacaf_leagues_cup',
  // Soccer — Asia/Pacific
  'J League': 'soccer_japan_j_league', 'K League': 'soccer_korea_kleague1',
  'A-League': 'soccer_australia_aleague', 'China SL': 'soccer_china_superleague',
  // Soccer — International
  'World Cup': 'soccer_fifa_world_cup', 'WCQ Europe': 'soccer_fifa_world_cup_qualifiers_europe',
  'WCQ CONMEBOL': 'soccer_fifa_world_cup_qualifiers_south_america',
  WCQ: 'soccer_fifa_world_cup_qualifiers', WWC: 'soccer_fifa_world_cup_womens',
  'Club WC': 'soccer_fifa_club_world_cup',
  Euro: 'soccer_uefa_european_championship', 'Euro Qual': 'soccer_uefa_euro_qualification',
  'Copa America': 'soccer_conmebol_copa_america', AFCON: 'soccer_africa_cup_of_nations',
  'Nations Lge': 'soccer_uefa_nations_league', Friendlies: 'soccer_international_friendlies',
  // Golf
  Golf: 'golf_pga_tour',
  // Tennis
  Tennis: 'tennis_atp_us_open',
  // Aussie Rules
  AFL: 'aussierules_afl',
  // Rugby
  NRL: 'rugbyleague_nrl', 'NRL Origin': 'rugbyleague_nrl_state_of_origin',
  'Six Nations': 'rugbyunion_six_nations',
  'Super Rugby': 'rugbyunion_super_rugby', 'Rugby WC': 'rugbyunion_rugby_world_cup',
  // Cricket
  Cricket: 'cricket_test_match', IPL: 'cricket_ipl',
  // Motorsport
  F1: 'motorsport_formula_one', NASCAR: 'motorsport_nascar',
  // Handball
  Handball: 'handball_germany_bundesliga',
};

// ─── SETTLE ONE PICK ──────────────────────────────────────────────────────────
// Sport-specific score plausibility ranges.
// If a game result has scores outside these bounds, it's almost certainly
// the wrong game data (e.g. a soccer score applied to an NBA pick).
// We reject it rather than settle incorrectly.
const SCORE_SANITY = {
  NBA:   { min: 60,  max: 180 },
  WNBA:  { min: 40,  max: 130 },
  NCAAB: { min: 30,  max: 150 },
  NFL:   { min: 0,   max: 80  },
  NCAAF: { min: 0,   max: 90  },
  NHL:   { min: 0,   max: 15  },
  MLB:   { min: 0,   max: 30  },
  MLS:   { min: 0,   max: 15  },
  EPL:   { min: 0,   max: 15  },
  Soccer:{ min: 0,   max: 15  },
  AFL:   { min: 20,  max: 200 },
  NRL:   { min: 0,   max: 70  },
  Cricket:{ min: 0,  max: 500 },
  IPL:   { min: 0,   max: 300 },
  // MMA, Boxing, Tennis, Golf, F1, NASCAR: no score-based sanity (different scoring systems)
};

export function settlePick(pick, gameResult) {
  const { homeScore, awayScore } = gameResult;

  // Guard: null/undefined scores must be checked BEFORE Number() conversion
  // because Number(null) === 0 which is not NaN — a null score would silently
  // become 0 and produce wrong results.
  if (homeScore == null || awayScore == null) return null;
  // Normalize types — API/Firestore can return strings for scores or lines
  const hs = Number(homeScore);
  const as = Number(awayScore);
  if (isNaN(hs) || isNaN(as)) return null;

  // Normalize pick fields — side may be 'Over'/'OVER', type may be 'Total'/'ML' etc.
  const type = (pick.type || '').toLowerCase();
  const side = (pick.side || '').toLowerCase();
  const line = pick.line != null ? Number(pick.line) : null;

  // Sport-specific sanity check: reject scores that are out of range for the sport.
  // This prevents a wrong-game match from producing an incorrect settlement.
  const sport  = pick.sport || '';
  const sanity = SCORE_SANITY[sport];
  if (sanity) {
    if (hs < sanity.min || hs > sanity.max || as < sanity.min || as > sanity.max) {
      console.warn(
        `[settlePick] Score out of range for ${sport}: ${as}–${hs} ` +
        `(expected ${sanity.min}–${sanity.max}). Refusing to settle — likely wrong game data.`
      );
      return null; // keep pick pending; retry next poll cycle
    }
  }

  // Spread line sanity check: reject implausible lines for the sport.
  const MAX_SPREAD = { NBA:22, NCAAB:30, NFL:28, NCAAF:45, NHL:3, MLB:4, Soccer:4, MLS:4, EPL:4 };
  if ((type === 'spread' || type === 'spread_1h' || type === 'alt_spread') && line != null && MAX_SPREAD[sport] != null) {
    if (Math.abs(line) > MAX_SPREAD[sport]) {
      console.warn(
        `[settlePick] Spread line ${line} is implausible for ${sport} ` +
        `(max expected: ±${MAX_SPREAD[sport]}). Refusing to settle — likely bad odds data.`
      );
      return null;
    }
  }

  console.info(`[settlePick] type=${type} side=${side} line=${line} hs=${hs} as=${as} sport=${sport}`);

  // ── MONEYLINE (full game + 1st half) ──────────────────────────────────────
  // winnerSide ('home'|'away'|null) is set for soccer matches decided in ET/PK
  // where the regulation score is level (sourced from ESPN's competitor.winner
  // flag). When present it OVERRIDES the score comparison — a 1-1 penalty win
  // is a real moneyline win, not a push.
  if (type === 'ml' || type === 'ml_1h') {
    const winnerSide = gameResult.winnerSide === 'home' || gameResult.winnerSide === 'away'
      ? gameResult.winnerSide : null;
    // A 'draw' pick wins only on a TRUE draw — level score AND nobody advanced.
    if (side === 'draw') return (hs === as && !winnerSide) ? 'won' : 'lost';
    let winningSide = winnerSide;
    if (!winningSide) { if (hs === as) return 'push'; winningSide = hs > as ? 'home' : 'away'; }
    if (side === 'home') return winningSide === 'home' ? 'won' : 'lost';
    if (side === 'away') return winningSide === 'away' ? 'won' : 'lost';
  }

  // ── SPREAD (full game + 1st half + alt spread) ────────────────────────────
  if (type === 'spread' || type === 'spread_1h' || type === 'alt_spread') {
    if (line == null || isNaN(line)) return null;
    const margin = hs - as;
    const cover = side === 'home' ? margin + line : -(margin) + line;
    if (Math.abs(cover) < 0.001) return 'push';
    return cover > 0 ? 'won' : 'lost';
  }

  // ── TOTAL (full game + 1st half + alt total) ──────────────────────────────
  if (type === 'total' || type === 'total_1h' || type === 'alt_total') {
    if (line == null || isNaN(line)) return null;
    const combined = hs + as;
    console.info(`[settlePick] total=${combined} vs line=${line} → ${side === 'over' ? (combined > line ? 'WON' : 'LOST') : (combined < line ? 'WON' : 'LOST')}`);
    if (Math.abs(combined - line) < 0.001) return 'push';
    if (side === 'over')  return combined > line ? 'won' : 'lost';
    if (side === 'under') return combined < line ? 'won' : 'lost';
    console.warn(`[settlePick] Unknown side="${pick.side}" for total pick — cannot settle`);
    return null;
  }

  // ── TEAM TOTAL ────────────────────────────────────────────────────────────
  // Over/under on a single team's score (not the combined total).
  // side stores "over"/"under" for the O/U direction; we determine which team's
  // score to use from the pick's side field fallback or the label containing
  // the home team name.
  if (type === 'team_total') {
    if (line == null || isNaN(line)) return null;
    // S-2 FIX: prefer the explicit teamSide persisted at pick time. Fall back to
    // the legacy label/side inference only for older picks that predate the field.
    // The old inference (side==='home' || label.includes(homeTeam)) mis-graded
    // when the team name was an abbreviation or one team's name was a substring
    // of the other's.
    const homeTeam = gameResult.homeTeam || '';
    let isHome;
    if (pick.teamSide === 'home') isHome = true;
    else if (pick.teamSide === 'away') isHome = false;
    else isHome = side === 'home' || (pick.label || '').includes(homeTeam);
    const teamScore = isHome ? hs : as;
    if (Math.abs(teamScore - line) < 0.001) return 'push';
    // For team totals, side encoding varies: some picks store "over"/"under"
    // directly, others store "home"/"away". Handle both.
    const direction = (side === 'over' || side === 'under') ? side
      : (pick.overUnder || '').toLowerCase();
    if (direction === 'over')  return teamScore > line ? 'won' : 'lost';
    if (direction === 'under') return teamScore < line ? 'won' : 'lost';
    // Fallback: if label contains O or U indicator
    const label = (pick.label || '').toUpperCase();
    if (label.includes(' O '))  return teamScore > line ? 'won' : 'lost';
    if (label.includes(' U '))  return teamScore < line ? 'won' : 'lost';
    console.warn(`[settlePick] Cannot determine O/U direction for team_total pick ${pick.id}`);
    return null;
  }

  // Player props are settled via a separate path (settlePlayerProp) — not an error.
  if (type === 'player_prop') return null;

  // Outright/tournament picks (e.g. Masters winner, Top 5 finish) are settled manually
  // by the captain/admin via TournamentSettleTool — not via game scores.
  if (type === 'outright' || type === 'top5' || type === 'top10' || type === 'top20' || type === 'make_cut') return null;

  console.warn(`[settlePick] Unknown type="${pick.type}" — cannot settle`);
  return null;
}

// ─── SETTLE ONE PLAYER PROP PICK ────────────────────────────────────────────
// Grades a player prop over/under pick by comparing the actual stat value
// against the line.  Returns 'won' | 'lost' | 'push' | null.
//
// `actualStatValue` is fetched from ESPN box score by the caller.
//
//   Example: pick.side = "over", pick.line = 24.5, actualStatValue = 28
//            → 28 > 24.5 → "won"
//
//   Push: actual === line (whole-number lines like 25.0)
//   Null: returned if actual is null/undefined (game not final or player DNP)
export function settlePlayerProp(pick, actualStatValue) {
  if (actualStatValue == null) return null;

  const actual = Number(actualStatValue);
  if (isNaN(actual)) return null;

  const line = pick.line != null ? Number(pick.line) : null;
  if (line == null || isNaN(line)) {
    console.warn(`[settlePlayerProp] Missing/invalid line for pick ${pick.id}`);
    return null;
  }

  const side = (pick.side || '').toLowerCase();

  // Exact push (whole-number lines)
  if (Math.abs(actual - line) < 0.001) return 'push';

  if (side === 'over')  return actual > line ? 'won' : 'lost';
  if (side === 'under') return actual < line ? 'won' : 'lost';

  console.warn(`[settlePlayerProp] Unknown side="${pick.side}" — cannot settle`);
  return null;
}

// ─── FETCH SCORES FOR PENDING PICKS ──────────────────────────────────────────
async function fetchResultsForPicks(pendingPicks) {
  const sportKeyToGameIds = {};
  for (const pick of pendingPicks) {
    const gameLegs = pick.isParlay && pick.legs ? pick.legs : [pick];
    for (const leg of gameLegs) {
      // ODDS-API-VOLUME (fix 2.1, 2026-09-28): the root runner calls this every
      // 90 s per open app for ANY pending pick -- including picks on games days
      // away, which can't have a result. Skip legs that can't use an Odds API
      // score: player props (graded from ESPN), games that started < 90 min
      // ago or not at all (isUsableResult rejects those results anyway), and
      // games > 4 days old (daysFrom=3 can't return them; ESPN fallback and the
      // server handle those). No commence time -> still fetched, as before.
      if ((leg.type || '').toLowerCase() === 'player_prop') continue;
      const legStart = toDate(leg.commenceTime) || (leg === pick ? null : toDate(pick.commenceTime));
      if (legStart) {
        const ageMs = Date.now() - legStart.getTime();
        if (ageMs < 90 * 60 * 1000 || ageMs > 4 * 24 * 60 * 60 * 1000) continue;
      }
      const key = leg.sportKey || SPORT_TO_KEY[leg.sport];
      if (!key) { console.warn(`[Settlement] No sport key for sport="${leg.sport}"`); continue; }
      if (!sportKeyToGameIds[key]) sportKeyToGameIds[key] = new Set();
      sportKeyToGameIds[key].add(leg.gameId);
    }
  }

  if (Object.keys(sportKeyToGameIds).length === 0) {
    console.warn('[Settlement] No sport keys resolved for any pending picks');
    return {};
  }

  const results = {};

  // Helper: only accept a game result if it's truly final and has real scores.
  // Guards against the API returning completed:true with 0-0 scores (data lag bug).
  const isUsableResult = (game) => {
    if (!game?.completed) return false;
    // CLIENT-DATE-GUARDS (fix 1.4, 2026-09-28): same 90-minute minimum age
    // the server applies before trusting the Odds API's completed flag
    // (functions/index.js settleAllPicks scores fetch). The client had no
    // equivalent, and until fetchScores carried commence_time it couldn't.
    if (game.commence_time) {
      const ageMs = Date.now() - new Date(game.commence_time).getTime();
      if (!isNaN(ageMs) && ageMs < 90 * 60 * 1000) {
        console.warn(`[Settlement] Skipping game ${game.id} -- completed:true but only ${Math.round(ageMs / 60000)}m since commence. Will retry.`);
        return false;
      }
    }
    const hs = Number(game.homeScore);
    const as = Number(game.awayScore);
    if (isNaN(hs) || isNaN(as)) return false;
    // Reject 0-0 completed non-soccer games — almost always API data-propagation lag.
    // Soccer 0-0 is a legitimate result (nil-nil), so we allow it for soccer sport keys.
    const isSoccer = (game.sportKey || '').startsWith('soccer_');
    if (hs === 0 && as === 0 && !isSoccer) {
      console.warn(`[Settlement] Skipping game ${game.id} — completed:true but 0-0 scores (likely API lag). Will retry.`);
      return false;
    }
    // Reject scores that look like they came from the wrong sport entirely.
    // e.g. an NHL game returning scores of 112-108 is impossible — that's a basketball score.
    // We use the sportKey prefix to infer sport family.
    const sk = game.sportKey || '';
    if (sk.startsWith('icehockey_') && (hs > 20 || as > 20)) {
      console.warn(`[Settlement] Skipping game ${game.id} — hockey scores ${as}–${hs} out of range.`);
      return false;
    }
    if (sk.startsWith('basketball_') && (hs < 40 || as < 40 || hs > 200 || as > 200)) {
      console.warn(`[Settlement] Skipping game ${game.id} — basketball scores ${as}–${hs} out of range.`);
      return false;
    }
    if (sk.startsWith('americanfootball_') && (hs > 100 || as > 100)) {
      console.warn(`[Settlement] Skipping game ${game.id} — football scores ${as}–${hs} out of range.`);
      return false;
    }
    return true;
  };

  for (const [sportKey, neededGameIds] of Object.entries(sportKeyToGameIds)) {
    try {
      const recent = await fetchScores(sportKey, 3);
      for (const game of recent) {
        if (isUsableResult(game)) results[game.id] = game;
      }

      // ODDS-API-VOLUME (fix 2.1): the 7/14/30-day retries are gone. The Odds
      // API only accepts daysFrom 1-3 (functions/index.js notes the 422s), so
      // each retry was a wasted request -- and 422s aren't cached, so they
      // repeated on every 90-second pass.

      // Log what was found vs needed
      const found = [...neededGameIds].filter(id => results[id]);
      const missing = [...neededGameIds].filter(id => !results[id]);
      if (found.length > 0) console.info(`[Settlement] ${sportKey}: found ${found.length} complete result(s)`);
      if (missing.length > 0) console.warn(`[Settlement] ${sportKey}: ${missing.length} game(s) not yet in scores API — picks remain pending`);
    } catch (e) { console.warn(`[Settlement] Scores failed for ${sportKey}:`, e.message); }
    await new Promise(r => setTimeout(r, 200));
  }
  return results;
}

// ─── SETTLEMENT LOG ──────────────────────────────────────────────────────────
// Writes a lightweight record to Firestore for every settlement attempt.
// Viewable in the Profile page settlement log panel.
// Fields: pickId, pickLabel, sport, type, side, line, status, homeScore, awayScore,
//         sportKey, profit, balanceDelta, settledAt, error (if any)
export async function writeSettlementLog(userId, entries) {
  if (!entries?.length) return;
  const col = collection(db, 'users', userId, 'settlementLog');
  await Promise.all(
    entries.map(e =>
      addDoc(col, { ...e, settledAt: serverTimestamp() }).catch(() => {})
    )
  );
}

export async function loadSettlementLog(userId, count = 50) {
  try {
    const q = query(
      collection(db, 'users', userId, 'settlementLog'),
      orderBy('settledAt', 'desc'),
      limit(count)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data(),
      settledAt: d.data().settledAt?.toDate?.() ?? null,
    }));
  } catch (e) {
    console.error('[Settlement] loadSettlementLog failed:', e.message);
    return [];
  }
}

// ─── MAIN: settleUserPicks ────────────────────────────────────────────────────
// preloadedScores: optional map of { [gameId]: { homeScore, awayScore, completed } }
// from the live-score polling loop. When provided, we use these scores directly
// and only fall back to fetchResultsForPicks for picks not covered by preloaded data.
// This prevents the "detect via live poll → re-fetch via scores API" divergence bug.
export async function settleUserPicks(userId, pendingPicks, preloadedScores = {}) {
  if (!userId || pendingPicks.length === 0) return [];
  console.info(`[Settlement] Attempting to settle ${pendingPicks.length} picks for user ${userId}`);

  // Filter preloadedScores through the same 0-0 guard used in fetchResultsForPicks.
  // Without this, a completed game with 0-0 scores in preloadedScores would bypass
  // isUsableResult() and wrongly settle over picks as a loss.
  // We also keep in-progress games (completed=false) so the polling loop can still
  // update live score state without accidentally triggering settlement on them.
  const safePreloaded = Object.fromEntries(
    Object.entries(preloadedScores).filter(([, g]) => {
      if (!g?.completed) return true; // keep in-progress entries for live display
      const hs = Number(g.homeScore);
      const as = Number(g.awayScore);
      if (isNaN(hs) || isNaN(as)) return false;
      const isSoccer = (g.sportKey || '').startsWith('soccer_');
      if (hs === 0 && as === 0 && !isSoccer) {
        console.warn(`[Settlement] Rejecting preloaded 0-0 score for game ${g.id || '?'} — likely API lag`);
        return false;
      }
      // Reject sport-family impossibilities from preloaded data too
      const sk = g.sportKey || '';
      if (sk.startsWith('icehockey_') && (hs > 20 || as > 20)) {
        console.warn(`[Settlement] Rejecting preloaded hockey score ${as}–${hs} for game ${g.id || '?'} — out of range`);
        return false;
      }
      if (sk.startsWith('basketball_') && (hs < 40 || as < 40 || hs > 200 || as > 200)) {
        console.warn(`[Settlement] Rejecting preloaded basketball score ${as}–${hs} for game ${g.id || '?'} — out of range`);
        return false;
      }
      if (sk.startsWith('americanfootball_') && (hs > 100 || as > 100)) {
        console.warn(`[Settlement] Rejecting preloaded football score ${as}–${hs} for game ${g.id || '?'} — out of range`);
        return false;
      }
      return true;
    })
  );

  // Fetch scores for any pick whose game isn't already covered by safe preloaded data.
  // Skip outright/tournament picks — they don't have game scores, settled manually
  const OUTRIGHT_TYPES = new Set(['outright', 'top5', 'top10', 'top20', 'make_cut']);
  const settlablePicks = pendingPicks.filter(p => !OUTRIGHT_TYPES.has((p.type || '').toLowerCase()));

  const picksNeedingFetch = settlablePicks.filter(p => {
    const legs = p.isParlay && p.legs ? p.legs : [p];
    return legs.some(leg => !safePreloaded[leg.gameId]?.completed);
  });
  const fetchedResults = picksNeedingFetch.length > 0
    ? await fetchResultsForPicks(picksNeedingFetch)
    : {};
  // fetchedResults wins on conflict — it has gone through isUsableResult() + tiered lookback.
  // safePreloaded fills in games not yet in the scores API (very recent completions).
  const gameResults = { ...safePreloaded, ...fetchedResults };

  if (Object.keys(gameResults).length === 0) {
    console.warn('[Settlement] No game results found — picks remain pending');
    return [];
  }

  const settled = [];
  const logEntries = []; // settlement log entries, flushed to Firestore at end

  for (const pick of settlablePicks) {
    // ── PARLAY SETTLEMENT ──────────────────────────────────────────────────
    if (pick.isParlay && Array.isArray(pick.legs)) {
      const legs = pick.legs;
      // PARLAY-ANY-LEG-LOST (fix 1.3, 2026-09-28): mirrors functions/index.js.
      // An unresolved leg marks the ticket unsettled and the loop CONTINUES,
      // so a lost leg later on the ticket still decides it. Previously the
      // first unresolved leg `break`-ed out, the ticket stayed pending even
      // with a losing leg, and the result depended on leg order.
      let allSettled = true, anyLost = false, combinedDecimal = 1;

      for (const leg of legs) {
        const legType = (leg.type || '').toLowerCase();
        let legStatus = null;

        if (legType === 'player_prop') {
          // ── Player prop leg: settle via ESPN box score ──────────────────
          // Same 2h gate as the server: a prop can't be final before its game.
          const legPropStart = toDate(leg.commenceTime);
          if (legPropStart && Date.now() - legPropStart.getTime() < 2 * 60 * 60 * 1000) { allSettled = false; continue; }
          try {
            const espnResult = await getPlayerStatForSettlement(leg);
            if (!espnResult) { allSettled = false; continue; }
            legStatus = settlePlayerProp(leg, espnResult.actual);
          } catch (e) {
            console.warn(`[Settlement] Parlay prop leg failed for ${leg.label}:`, e.message);
          }
        } else {
          // ── Game-score leg: settle via scores API ───────────────────────
          let result = gameResults[leg.gameId];

          // Same date-drift guard as the straight-pick path. If the result's
          // commenceTime is more than 24h off from leg.commenceTime, reject.
          // ⚠️ leg.commenceTime is a Firestore Timestamp -- toDate() required.
          if (result && leg.commenceTime) {
            const resultIso = result.commence_time || result.commenceTime || result.startTime || result.date || null;
            const legDate = toDate(leg.commenceTime);
            if (resultIso && legDate) {
              const resultMs = new Date(resultIso).getTime();
              if (!isNaN(resultMs)) {
                const driftMs = Math.abs(resultMs - legDate.getTime());
                const MAX_LEG_DRIFT_MS = 12 * 60 * 60 * 1000; // was 24h — a Sat-night/Sun-day series turnaround is ~18h and slipped inside; 12h separates every consecutive-day pairing while containing rain delays
                if (driftMs > MAX_LEG_DRIFT_MS) {
                  console.warn(`[Settlement] Rejecting gameResult for parlay leg ${leg.gameId} — date drift ${Math.round(driftMs / 3600000)}h. Likely wrong-game data.`);
                  result = null;
                }
              }
            }
          }

          // ESPN FALLBACK for parlay legs — same time-window guard as the
          // straight-pick path above. See the long comment there for why.
          // A parlay leg on a future game must not match an unrelated prior
          // meeting of the same teams.
          const MIN_LEG_GAME_LENGTH_MS = 4 * 60 * 60 * 1000;
          const legCommenceDate = toDate(leg.commenceTime);
          const legGameShouldBeComplete = legCommenceDate != null
            && (Date.now() - legCommenceDate.getTime()) >= MIN_LEG_GAME_LENGTH_MS;
          if (!result && legGameShouldBeComplete && leg.homeTeamFull && leg.awayTeamFull && leg.commenceTime) {
            try {
              const espnScore = await getGameScoreFromESPN(leg);
              if (espnScore) {
                result = espnScore;
                gameResults[leg.gameId] = espnScore;
                console.info(`[Settlement] ESPN fallback found score for parlay leg ${leg.gameId}`);
              }
            } catch (e) {
              console.warn(`[Settlement] ESPN fallback failed for parlay leg ${leg.gameId}:`, e.message);
            }
          }
          if (!result) { allSettled = false; continue; }
          legStatus = settlePick(leg, result);
        }

        if (!legStatus) { allSettled = false; continue; }
        if (legStatus === 'lost') { anyLost = true; break; }
        if (legStatus === 'push') continue;
        const legOdds = leg.odds || -110;
        const dec = legOdds > 0 ? 1 + legOdds / 100 : 1 + 100 / Math.abs(legOdds);
        combinedDecimal *= dec;
      }

      if (!allSettled && !anyLost) continue;

      let parlayStatus, parlayProfit, parlayBalance;
      if (anyLost) {
        parlayStatus = 'lost'; parlayProfit = 0; parlayBalance = 0;
      } else if (combinedDecimal <= 1) {
        parlayStatus = 'push'; parlayProfit = 0; parlayBalance = +(pick.wager || 0);
      } else {
        const payout = Math.round((pick.wager || 0) * combinedDecimal);
        parlayStatus  = 'won';
        parlayProfit  = payout - (pick.wager || 0); // profit for display
        parlayBalance = payout;                      // FIX: full payout for balance
      }

      // ── IDEMPOTENCY GUARD (parlay) ─────────────────────────────────────────
      const parlayRef = doc(db, 'picks', userId, 'userPicks', pick.id);
      // fix 1.5: status + balance + league credit in ONE transaction.
      let parlayRes = { did: false };
      try {
        parlayRes = await settleAndCredit(userId, pick,
          { status: parlayStatus, points: parlayProfit, settledAt: new Date() }, parlayStatus, parlayBalance);
      } catch (e) { console.error(`[Settlement] Parlay transaction failed ${parlayRef.path}:`, e.message); }

      if (!parlayRes.did) continue;

      console.info(`[Settlement] Parlay ${pick.id} → ${parlayStatus} | profit:${parlayProfit} | bal+:${parlayBalance}`);
      settled.push({ ...pick, status: parlayStatus, points: parlayProfit, ...(parlayRes.leagueCredited ? { leagueCredited: true } : {}) });
      logEntries.push({
        pickId:       pick.id,
        pickLabel:    pick.label || `${pick.legCount || legs.length}-leg Parlay`,
        sport:        pick.sport || legs[0]?.sport || '',
        sportKey:     legs[0]?.sportKey || '',
        type:         'parlay',
        side:         '',
        line:         null,
        homeScore:    null,
        awayScore:    null,
        status:       parlayStatus,
        profit:       parlayProfit,
        balanceDelta: parlayBalance,
      });
      continue;
    }

    // ── PLAYER PROP SETTLEMENT ────────────────────────────────────────────
    // Player props are settled via ESPN box score data, not game scores.
    // We detect player_prop type and route to the ESPN-based grading path.
    const pickType = (pick.type || '').toLowerCase();
    if (pickType === 'player_prop') {
      try {
        const espnResult = await getPlayerStatForSettlement(pick);
        if (!espnResult) {
          // ESPN data not available yet — pick stays pending
          logEntries.push({
            pickId:    pick.id,
            pickLabel: pick.label || '',
            sport:     pick.sport    || '',
            sportKey:  pick.sportKey || '',
            type:      'player_prop',
            side:      pick.side     || '',
            line:      pick.line     ?? null,
            gameId:    pick.gameId   || '',
            homeScore: null,
            awayScore: null,
            status:    'pending',
            profit:    null,
            balanceDelta: 0,
            reason:    'ESPN box score not yet available or player not found',
          });
          continue;
        }

        const propStatus = settlePlayerProp(pick, espnResult.actual);
        if (!propStatus) {
          logEntries.push({
            pickId:    pick.id,
            pickLabel: pick.label || '',
            sport:     pick.sport    || '',
            sportKey:  pick.sportKey || '',
            type:      'player_prop',
            side:      pick.side     || '',
            line:      pick.line     ?? null,
            gameId:    pick.gameId   || '',
            homeScore: null,
            awayScore: null,
            status:    'pending',
            profit:    null,
            balanceDelta: 0,
            reason:    `Could not grade prop: actual=${espnResult.actual} side="${pick.side}" line=${pick.line}`,
          });
          continue;
        }

        const wager    = pick.wager || 0;
        const american = pick.odds  || 0;
        const profit   = netPoints(propStatus, wager, american);
        const balChg   = balanceDeltaForResult(propStatus, wager, american);

        // Idempotency guard — same transaction pattern as straight picks
        const propRef = doc(db, 'picks', userId, 'userPicks', pick.id);
        // fix 1.5: status + balance + league credit in ONE transaction.
        let propRes = { did: false };
        try {
          propRes = await settleAndCredit(userId, pick, {
            status:    propStatus,
            points:    profit,
            settledAt: new Date(),
            actualStat: espnResult.actual,     // Store for UI display
            espnEventId: espnResult.espnEventId,
          }, propStatus, balChg);
        } catch (e) {
          console.error(`[Settlement] Player prop transaction failed ${propRef.path}:`, e.message);
        }

        if (!propRes.did) continue;

        console.info(`[Settlement] Prop ${pick.id} → ${propStatus} | actual:${espnResult.actual} vs line:${pick.line} | profit:${profit} | bal+:${balChg}`);
        settled.push({ ...pick, status: propStatus, points: profit, actualStat: espnResult.actual, ...(propRes.leagueCredited ? { leagueCredited: true } : {}) });
        logEntries.push({
          pickId:       pick.id,
          pickLabel:    pick.label || '',
          sport:        pick.sport || '',
          sportKey:     pick.sportKey || '',
          type:         'player_prop',
          side:         pick.side  || '',
          line:         pick.line  ?? null,
          gameId:       pick.gameId || '',
          homeScore:    null,
          awayScore:    null,
          actualStat:   espnResult.actual,
          status:       propStatus,
          profit,
          balanceDelta: balChg,
        });
      } catch (e) {
        console.warn(`[Settlement] Player prop settlement error for ${pick.id}:`, e.message);
      }
      continue;
    }

    // ── STRAIGHT PICK SETTLEMENT ────────────────────────────────────────────
    let result = gameResults[pick.gameId];

    // ⚠️ gameResults DATE-DRIFT GUARD:
    // The Odds API can re-use or alias game IDs across same-matchup playoff
    // series games — Game 1 and Game 3 of a Vegas-vs-Colorado series may end
    // up with overlapping gameIds in our cache. fetchScores returns the
    // already-completed Game 1's score keyed by that gameId, and a pick on
    // Game 3 (still upcoming) inherits it. This also catches the hop-on
    // case where a hopped pick inherits a gameId that resolves to a
    // different game than what the pick's commenceTime represents.
    //
    // Reject the result if its commenceTime/start time differs from the
    // pick's commenceTime by more than 24h. The result then falls through
    // to the ESPN fallback below, which has its own date verification.
    // ⚠️ pick.commenceTime can be a Firestore Timestamp -- toDate() required.
    if (result && pick.commenceTime) {
      const resultIso = result.commence_time || result.commenceTime || result.startTime || result.date || null;
      const pickDate = toDate(pick.commenceTime);
      if (resultIso && pickDate) {
        const resultMs = new Date(resultIso).getTime();
        if (!isNaN(resultMs)) {
          const driftMs = Math.abs(resultMs - pickDate.getTime());
          const MAX_DRIFT_MS = 12 * 60 * 60 * 1000; // was 24h — see series-drift note above; same tightening
          if (driftMs > MAX_DRIFT_MS) {
            console.warn(`[Settlement] Rejecting gameResult for pick ${pick.id} — date drift ${Math.round(driftMs / 3600000)}h between result (${resultIso}) and pick.commenceTime. Likely wrong-game data.`);
            result = null;
          }
        }
      }
    }

    // ESPN FALLBACK: if Odds API scores don't have this game, try ESPN.
    //
    // ⚠️ CRITICAL GUARD — DO NOT REMOVE WITHOUT CAREFUL CONSIDERATION:
    // Without this time-window check, the fallback matches games by team
    // names alone and can return a PRIOR series game's score for a pick on
    // an upcoming game. Seen in the wild on May 22 2026 — a pick placed on
    // tonight's Avalanche vs Knights playoff game got settled with the 4-2
    // final score from a prior series game (Game 1, two days earlier),
    // because that's the most recent Avs-vs-Knights matchup ESPN returns
    // when queried by team names.
    //
    // The 4-hour buffer covers the maximum regulation+OT length across NHL/
    // NBA/NFL/MLB/soccer. If the game's scheduled commence time + 4h hasn't
    // passed yet, the game can't possibly be final — so we should NOT be
    // matching ESPN scores at all. Picks stay pending; the next cron pass
    // re-attempts after the window opens.
    //
    // The root-cause fix belongs in espnStats.js (verify matched game's
    // date matches pick.commenceTime), but this guard is defense-in-depth
    // and protects against any future ESPN matching weakness.
    // ⚠️ pick.commenceTime can be a Firestore Timestamp -- toDate() required.
    const MIN_GAME_LENGTH_MS = 4 * 60 * 60 * 1000;
    const pickCommenceDate = toDate(pick.commenceTime);
    const gameShouldBeComplete = pickCommenceDate != null
      && (Date.now() - pickCommenceDate.getTime()) >= MIN_GAME_LENGTH_MS;

    if (!result && gameShouldBeComplete && pick.homeTeamFull && pick.awayTeamFull && pick.commenceTime) {
      try {
        const espnScore = await getGameScoreFromESPN(pick);
        if (espnScore) {
          result = espnScore;
          gameResults[pick.gameId] = espnScore; // cache for parlay legs referencing same game
          console.info(`[Settlement] ESPN fallback found score for ${pick.gameId}`);
        }
      } catch (e) {
        console.warn(`[Settlement] ESPN fallback failed for ${pick.gameId}:`, e.message);
      }
    } else if (!result && pickCommenceDate != null && !gameShouldBeComplete) {
      // Pre-game or in-progress — game shouldn't be settleable yet. Log
      // for traceability so we can see why a pick is still pending in the
      // admin settlement log view.
      const minsUntilSettleable = Math.ceil(
        (pickCommenceDate.getTime() + MIN_GAME_LENGTH_MS - Date.now()) / 60000
      );
      console.info(`[Settlement] Skipping ${pick.id} — game not yet in settlement window (${minsUntilSettleable}m to go)`);
    }

    if (!result) {
      // Log the miss so the UI shows WHY this pick is still pending
      logEntries.push({
        pickId:    pick.id,
        pickLabel: pick.label || pick.gameLabel || '',
        sport:     pick.sport    || '',
        sportKey:  pick.sportKey || '',
        type:      pick.type     || '',
        side:      pick.side     || '',
        line:      pick.line     ?? null,
        gameId:    pick.gameId   || '',
        homeScore: null,
        awayScore: null,
        status:    'pending',
        profit:    null,
        balanceDelta: 0,
        reason:    'No score data found (Odds API + ESPN fallback) — may be too early or sportKey missing',
      });
      continue;
    }

    const status = settlePick(pick, result);
    if (!status) {
      console.warn(`[Settlement] Could not determine outcome for pick ${pick.id}`);
      logEntries.push({
        pickId:    pick.id,
        pickLabel: pick.label || pick.gameLabel || '',
        sport:     pick.sport    || '',
        sportKey:  pick.sportKey || '',
        type:      pick.type     || '',
        side:      pick.side     || '',
        line:      pick.line     ?? null,
        gameId:    pick.gameId   || '',
        homeScore: result.homeScore,
        awayScore: result.awayScore,
        status:    'pending',
        profit:    null,
        balanceDelta: 0,
        reason:    `Unknown pick type/side: type="${pick.type}" side="${pick.side}"`,
      });
      continue;
    }

    const wager    = pick.wager || 0;
    const american = pick.odds  || 0;
    const profit   = netPoints(status, wager, american);             // for display
    const balChg   = balanceDeltaForResult(status, wager, american); // for balance

    // ── IDEMPOTENCY GUARD ───────────────────────────────────────────────────
    // Use a transaction to verify the pick is still "pending" before writing.
    // If two tabs both settle the same pick, only the first write goes through;
    // the second sees status !== "pending" and aborts — preventing double-credit.
    const pickRef = doc(db, 'picks', userId, 'userPicks', pick.id);
    // fix 1.5: status + balance + league credit in ONE transaction.
    let pickRes = { did: false };
    try {
      pickRes = await settleAndCredit(userId, pick, { status, points: profit, settledAt: new Date() }, status, balChg);
    } catch (e) {
      console.error(`[Settlement] Pick transaction failed ${pickRef.path}:`, e.message);
    }

    if (!pickRes.did) continue;

    console.info(`[Settlement] Pick ${pick.id} → ${status} | profit:${profit} | bal+:${balChg}`);
    settled.push({ ...pick, status, points: profit, ...(pickRes.leagueCredited ? { leagueCredited: true } : {}) });
    logEntries.push({
      pickId:       pick.id,
      pickLabel:    pick.label || pick.gameLabel || '',
      sport:        pick.sport || '',
      sportKey:     pick.sportKey || '',
      type:         pick.type  || '',
      side:         pick.side  || '',
      line:         pick.line  ?? null,
      homeScore:    result.homeScore,
      awayScore:    result.awayScore,
      status,
      profit,
      balanceDelta: balChg,
    });
  }

  // fix 1.5: no end-of-run balance apply -- each pick credited itself above.
  // Flush settlement log to Firestore (non-blocking — don't await)
  writeSettlementLog(userId, logEntries).catch(() => {});
  console.info(`[Settlement] Done — ${settled.length}/${pendingPicks.length} picks settled`);
  return settled;
}

// ─── BALANCE HELPERS ──────────────────────────────────────────────────────────
export async function applyBalanceDelta(userId, delta, label = null) {
  // Uses a Firestore transaction to eliminate the read-modify-write race condition.
  // Without this, two simultaneous settlements (e.g. two open tabs) could both read
  // the same balance, compute two different "next" values, and the last write wins —
  // silently losing one of the deltas.
  const userRef = doc(db, 'users', userId);
  let next;

  await runTransaction(db, async (txn) => {
    const snap = await txn.get(userRef);
    const cur  = snap.data()?.balance ?? 0;
    next = Math.max(BALANCE_FLOOR, cur + delta);
    txn.update(userRef, { balance: next });
  });

  // Balance history is best-effort — written outside the transaction so a history
  // write failure never rolls back a legitimate balance update.
  addDoc(collection(db, 'users', userId, 'balanceHistory'), {
    balance: next, delta, label: label || (delta >= 0 ? 'win' : 'loss'), createdAt: new Date(),
  }).catch(() => {});

  return next;
}

export async function getUserBalance(userId) {
  const snap = await getDoc(doc(db, 'users', userId));
  const data = snap.data();
  // Return null if the user doc doesn't exist OR balance field is absent.
  // This allows callers to distinguish "new user (null)" from "user with 0 balance (0)".
  if (!data || data.balance == null) return null;
  return data.balance;
}

// ─── FRIDAY PAYDAY ────────────────────────────────────────────────────────────
export async function checkAndApplyPayday(userId) {
  const now = new Date();

  // Find the most recent Friday (retroactive — pays out even if user missed Friday)
  // daysToLastFriday: 0 if today is Friday, 1 if Saturday, 2 if Sunday, etc.
  const dayOfWeek = now.getDay(); // 0=Sun, 5=Fri, 6=Sat
  const daysToLastFriday = dayOfWeek === PAYDAY_DAY
    ? 0
    : (dayOfWeek - PAYDAY_DAY + 7) % 7;
  const lastFriday = new Date(now);
  lastFriday.setDate(now.getDate() - daysToLastFriday);
  lastFriday.setHours(0, 0, 0, 0);

  // No payday yet this week — last Friday hasn't happened
  if (now < lastFriday) return null;

  const userRef = doc(db, 'users', userId);
  let newBalance = null;

  await runTransaction(db, async (txn) => {
    const snap = await txn.get(userRef);
    const data = snap.data() || {};
    const lastPayday = data.lastPayday?.toDate?.() ?? new Date(0);
    // Only pay if the user hasn't received this week's payday yet
    // (lastPayday before this past Friday = they missed it → pay now)
    if (lastPayday >= lastFriday) { newBalance = null; return; }
    newBalance = (data.balance ?? 0) + WEEKLY_PAYDAY;
    txn.set(userRef, { balance: newBalance, lastPayday: now }, { merge: true });
  });

  if (newBalance === null) return null;

  addDoc(collection(db, 'users', userId, 'balanceHistory'), {
    balance: newBalance, delta: WEEKLY_PAYDAY, label: 'payday', createdAt: now,
  }).catch(() => {});
  return WEEKLY_PAYDAY;
}

// ─── INIT BALANCE ─────────────────────────────────────────────────────────────
export async function initUserBalance(userId, startingBalance) {
  // Transaction prevents two simultaneous sign-ins (rare but possible) from
  // both seeding the balance and doubling the starting points.
  const userRef = doc(db, 'users', userId);
  let finalBalance;

  await runTransaction(db, async (txn) => {
    const snap = await txn.get(userRef);
    const data = snap.data() || {};
    if (data.balance == null) {
      txn.set(userRef, { balance: startingBalance }, { merge: true });
      finalBalance = startingBalance;
    } else {
      finalBalance = data.balance;
    }
  });

  return finalBalance;
}

// ─── BALANCE HISTORY ──────────────────────────────────────────────────────────
export async function loadBalanceHistory(userId, count = 30) {
  try {
    const q = query(collection(db, 'users', userId, 'balanceHistory'),
      orderBy('createdAt', 'asc'), limit(count));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('[Settlement] loadBalanceHistory failed:', e.message);
    return [];
  }
}

// ─── RE-SETTLE / VOID A WRONGLY-SETTLED PICK ─────────────────────────────────
// Used when a pick was settled incorrectly (e.g. due to a score data bug).
// Voids the pick and refunds the full wager to the user's balance.
// The pick status is set to "voided" with a voidReason for the audit trail.
//
// Usage: await voidAndRefundPick(userId, pickId, wager, reason)
export async function voidAndRefundPick(userId, pickId, wager, reason = "wrong settlement — refunded") {
  const pickRef  = doc(db, 'picks', userId, 'userPicks', pickId);
  const userRef  = doc(db, 'users', userId);

  // F5: capture the ACTUAL balance change applied (after floor clamping) so the
  // balanceHistory entry below reflects reality. The old code always logged
  // { delta: wager } regardless of what actually happened — wrong for won-voids
  // (real change is -profit), push-voids (real change is 0 → phantom entry), and
  // even lost-voids where the floor clamped the refund below `wager`. No balance
  // bug (the txn was correct), but the ledger lied, which defeats the audit trail.
  let appliedDelta = 0;
  let originalStatus = null;

  await runTransaction(db, async (txn) => {
    const pickSnap = await txn.get(pickRef);
    const pickData = pickSnap.data();
    if (!pickData) throw new Error(`Pick ${pickId} not found`);

    // Record original status for audit trail
    originalStatus = pickData.status;

    // Reverse the balance impact of the wrong settlement:
    // If it was settled as "lost"  → refund wager (wager was already deducted at submit, nothing was credited)
    // If it was settled as "won"   → deduct the profit that was wrongly credited (rare)
    // If it was settled as "push"  → was already refunded, no balance change needed
    let balanceDelta = 0;
    if (originalStatus === 'lost') {
      balanceDelta = wager; // refund the wager
    } else if (originalStatus === 'won') {
      // Subtract the profit that was wrongly credited
      // points field = profit credited; wager was already deducted at submit
      const wrongProfit = pickData.points ?? 0;
      balanceDelta = -wrongProfit;
    }
    // push: no delta needed (wager was already refunded at push settlement)

    txn.update(pickRef, {
      status:      'voided',
      points:      0,
      settledAt:   new Date(),
      voidReason:  reason,
      originalStatus,
    });

    if (balanceDelta !== 0) {
      const userSnap = await txn.get(userRef);
      const cur = userSnap.data()?.balance ?? 0;
      const next = Math.max(BALANCE_FLOOR, cur + balanceDelta);
      txn.update(userRef, { balance: next });
      // The real change is next-cur, which may differ from balanceDelta if the
      // floor clamped it. Record THAT, not the nominal wager.
      appliedDelta = next - cur;
    }
  });

  // Balance history entry (best-effort) — logs the ACTUAL applied delta. Skip
  // entirely when nothing moved (e.g. push-void) so we don't write a phantom row.
  if (appliedDelta !== 0) {
    addDoc(collection(db, 'users', userId, 'balanceHistory'), {
      delta: appliedDelta,
      label: 'void_refund',
      voidedFrom: originalStatus || null, // what it was before the void, for context
      createdAt: new Date(),
    }).catch(() => {});
  }

  console.info(`[Settlement] Pick ${pickId} voided (was ${originalStatus}); balance change ${appliedDelta >= 0 ? '+' : ''}${appliedDelta} pts for ${userId}`);
  // Return shape preserved for existing callers; `refunded` now reflects the
  // real applied change rather than the nominal wager.
  return { refunded: appliedDelta, originalStatus };
}
